// src/services/bi/FormationDetailModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import { ajouterSession } from '../../services/infoCentre/session_formation_service';
import { getFormateurs } from '../../services/infoCentre/Formateurservice';
import { useAuth } from '../../context/AuthContext';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getTodayDate = () => {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
};

const EMPTY_SESSION_FORM = {
  intitule_session: '',
  formation: '',
  formateurs: [],
  date_debut: '',
  date_fin: '',
  mode: 'presentiel',
  niveau: '',
  prix_ht: '',
  prix_ttc: '',
  tranche: '',
};

// ─── FormateursMultiSelect ────────────────────────────────────────────────────

function FormateursMultiSelect({ formateurs, value, onChange }) {
  const [search, setSearch] = useState('');
  const filtered = (formateurs || []).filter((f) =>
    `${f.prenom || ''} ${f.nom || ''} ${f.specialites || ''}`.toLowerCase().includes(search.toLowerCase())
  );
  const toggle = (id) => {
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  };

  return (
    <div className="fmt-multiselect-wrap">
      <div className="fmt-multiselect-search">
        <i className="fa-solid fa-magnifying-glass"></i>
        <input
          type="text"
          placeholder="Rechercher un formateur..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <button type="button" className="fmt-search-clear" onClick={() => setSearch('')}>
            <i className="fa-solid fa-xmark"></i>
          </button>
        )}
      </div>
      <div className="fmt-multiselect-list">
        {filtered.length === 0 ? (
          <div className="fmt-multiselect-empty">Aucun formateur trouvé</div>
        ) : (
          filtered.map((f) => {
            const checked = value.includes(f.id);
            const nomComplet = `${f.prenom || ''} ${f.nom || ''}`.trim();
            return (
              <div
                key={f.id}
                className={`fmt-multiselect-item ${checked ? 'checked' : ''}`}
                onClick={() => toggle(f.id)}
              >
                <span className={`fmt-checkbox ${checked ? 'checked' : ''}`}>
                  {checked && <i className="fa-solid fa-check" style={{ fontSize: '10px', color: '#fff' }}></i>}
                </span>
                <span className="fmt-name">
                  {nomComplet}
                  {f.specialites && (
                    <span style={{ fontSize: '11px', color: '#64748b', marginLeft: '5px' }}>
                      ({f.specialites.length > 35 ? f.specialites.substring(0, 35) + '...' : f.specialites})
                    </span>
                  )}
                </span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ─── OuvrirSessionForm ────────────────────────────────────────────────────────

function OuvrirSessionForm({ formation, onSuccess, onCancel, formateurs }) {
  const [form, setForm] = useState({
    ...EMPTY_SESSION_FORM,
    formation: formation.id,
    intitule_session: `Session - ${formation.intitule}`,
  });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [loading, setLoading] = useState(false);
  const topRef = useRef(null);

  const validate = () => {
    const errs = {};
    if (!form.intitule_session?.trim()) errs.intitule_session = "L'intitulé est requis";
    if (!form.date_debut) errs.date_debut = 'La date de début est requise';
    if (!form.date_fin) errs.date_fin = 'La date de fin est requise';
    if (form.date_debut && form.date_fin && form.date_debut >= form.date_fin)
      errs.date_fin = 'La date de fin doit être postérieure à la date de début';
    if (!form.prix_ht || parseFloat(form.prix_ht) < 0) errs.prix_ht = 'Le prix HT est requis';
    if (!form.prix_ttc || parseFloat(form.prix_ttc) < 0) errs.prix_ttc = 'Le prix TTC est requis';
    if (form.prix_ht && form.prix_ttc && parseFloat(form.prix_ht) > parseFloat(form.prix_ttc))
      errs.prix_ttc = 'Le prix TTC doit être ≥ au prix HT';
    if (!form.tranche || parseInt(form.tranche) < 1)
      errs.tranche = 'Le nombre de tranches est requis (≥ 1)';
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    try {
      setLoading(true);
      setErrors({});
      setServerError('');
      await ajouterSession({
        ...form,
        formation: parseInt(form.formation),
        prix_ht: parseFloat(form.prix_ht),
        prix_ttc: parseFloat(form.prix_ttc),
        tranche: parseInt(form.tranche),
        niveau: form.niveau === '' ? null : form.niveau,
      });
      onSuccess();
    } catch (err) {
      if (err.response?.data) {
        const d = err.response.data;
        if (d.dates) {
          setErrors({ dates: d.dates });
        } else {
          setServerError(Object.values(d).flat().join('\n'));
        }
      } else {
        setServerError('Erreur serveur. Veuillez réessayer.');
      }
      topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } finally {
      setLoading(false);
    }
  };

  const set = (key, val) => {
    setForm((prev) => ({ ...prev, [key]: val }));
    setErrors((prev) => ({ ...prev, [key]: '' }));
  };

  return (
    <div className="ouvrir-session-form">
      <div ref={topRef} />

      <div className="osf-formation-banner">
        <i className="fa-solid fa-lock"></i>
        <span>Formation : <strong>{formation.intitule}</strong></span>
      </div>

      {serverError && (
        <div className="error-server" style={{ margin: '0 0 16px' }}>
          <i className="fa-solid fa-circle-exclamation"></i> {serverError}
        </div>
      )}
      {errors.dates && (
        <div className="error-duration" style={{ margin: '0 0 16px' }}>
          <i className="fa-solid fa-clock"></i>
          <span>{errors.dates}</span>
        </div>
      )}

      <div className="form-grid">
        <div className="form-group full">
          <label>Intitulé de la session <span className="req">*</span></label>
          <input
            type="text"
            className={errors.intitule_session ? 'input-error' : ''}
            value={form.intitule_session}
            onChange={(e) => set('intitule_session', e.target.value)}
            placeholder="Nom de la session"
          />
          <small className="error-msg">{errors.intitule_session}</small>
        </div>

        <div className="form-group full">
          <label>Formateurs</label>
          <FormateursMultiSelect
            formateurs={formateurs}
            value={form.formateurs}
            onChange={(val) => set('formateurs', val)}
          />
        </div>

        <div className="form-group">
          <label>Date de début <span className="req">*</span></label>
          <input
            type="date"
            min={getTodayDate()}
            className={errors.date_debut ? 'input-error' : ''}
            value={form.date_debut}
            onChange={(e) => {
              set('date_debut', e.target.value);
              setErrors((p) => ({ ...p, date_fin: '' }));
            }}
          />
          <small className="error-msg">{errors.date_debut}</small>
        </div>

        <div className="form-group">
          <label>Date de fin <span className="req">*</span></label>
          <input
            type="date"
            min={form.date_debut || getTodayDate()}
            className={errors.date_fin ? 'input-error' : ''}
            value={form.date_fin}
            onChange={(e) => set('date_fin', e.target.value)}
          />
          <small className="error-msg">{errors.date_fin}</small>
        </div>

        <div className="form-group">
          <label>Mode</label>
          <select value={form.mode} onChange={(e) => set('mode', e.target.value)}>
            <option value="presentiel">Présentiel</option>
            <option value="ligne">En ligne</option>
            <option value="hybride">Hybride</option>
          </select>
        </div>

        <div className="form-group">
          <label>Niveau (optionnel)</label>
          <select value={form.niveau || ''} onChange={(e) => set('niveau', e.target.value)}>
            <option value="">-- Aucun --</option>
            <option value="debutant">Débutant</option>
            <option value="intermediaire">Intermédiaire</option>
            <option value="avance">Avancé</option>
          </select>
        </div>

        <div className="form-group">
          <label>Prix HT (DT) <span className="req">*</span></label>
          <input
            type="number" step="0.01" min="0"
            className={errors.prix_ht ? 'input-error' : ''}
            value={form.prix_ht}
            onChange={(e) => {
              set('prix_ht', e.target.value);
              setErrors((p) => ({ ...p, prix_ttc: '' }));
            }}
          />
          <small className="error-msg">{errors.prix_ht}</small>
        </div>

        <div className="form-group">
          <label>Prix TTC (DT) <span className="req">*</span></label>
          <input
            type="number" step="0.01" min="0"
            className={errors.prix_ttc ? 'input-error' : ''}
            value={form.prix_ttc}
            onChange={(e) => set('prix_ttc', e.target.value)}
          />
          <small className="error-msg">{errors.prix_ttc}</small>
        </div>

        <div className="form-group">
          <label>Nombre de tranches <span className="req">*</span></label>
          <input
            type="number" min="1"
            className={errors.tranche ? 'input-error' : ''}
            value={form.tranche}
            onChange={(e) => set('tranche', e.target.value)}
          />
          <small className="error-msg">{errors.tranche}</small>
        </div>
      </div>

      <div className="osf-actions">
        <button className="btn btn-cancel" onClick={onCancel} disabled={loading}>
          <i className="fa-solid fa-arrow-left"></i> Retour
        </button>
        <button className="btn btn-add" onClick={handleSubmit} disabled={loading}>
          {loading
            ? <><i className="fa-solid fa-spinner fa-spin"></i> Création...</>
            : <><i className="fa-solid fa-calendar-plus"></i> Créer la session</>}
        </button>
      </div>
    </div>
  );
}

// ─── Main Modal ───────────────────────────────────────────────────────────────

const FormationDetailModal = ({ formation, isOpen, onClose }) => {
  // ✅ OPTIMISATION : on utilise directement formation.prospects_chauds
  // qui est déjà chargé dans la card — AUCUN appel API supplémentaire
  const prospects = formation?.prospects_chauds || [];

  // 🔒 Le bouton "Ouvrir une session" est réservé au super admin
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const [view, setView] = useState('prospects');
  const [successMsg, setSuccessMsg] = useState('');

  // Formateurs chargés UNE SEULE FOIS à l'ouverture de la modal
  const [formateurs, setFormateurs] = useState([]);
  const formateursLoaded = useRef(false);

  useEffect(() => {
    if (isOpen) {
      setView('prospects');
      setSuccessMsg('');
      // Préchargement des formateurs en arrière-plan dès l'ouverture
      // (non bloquant — l'onglet prospects s'affiche immédiatement)
      if (!formateursLoaded.current) {
        getFormateurs()
          .then((data) => {
            setFormateurs(Array.isArray(data) ? data : []);
            formateursLoaded.current = true;
          })
          .catch(() => setFormateurs([]));
      }
    }
  }, [isOpen]);

  const handleSessionCreated = () => {
    setSuccessMsg(`✅ Session créée avec succès pour "${formation.intitule}" !`);
    setView('prospects');
    setTimeout(() => setSuccessMsg(''), 5000);
  };

  if (!isOpen || !formation) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-container fdm-container"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: view === 'ouvrir-session' ? '720px' : '900px' }}
      >
        {/* ── Header ── */}
        <div className="modal-header fdm-header">
          <div className="fdm-header-left">
            {view === 'ouvrir-session' && (
              <button
                className="fdm-back-btn"
                onClick={() => setView('prospects')}
                title="Retour à la liste"
              >
                <i className="fa-solid fa-arrow-left"></i>
              </button>
            )}
            <div>
              <h3 className="modal-title">
                {view === 'ouvrir-session'
                  ? <><i className="fa-solid fa-calendar-plus" style={{ color: '#10b981', marginRight: 8 }}></i>Ouvrir une session</>
                  : <>📋 {formation.intitule}</>}
              </h3>
              {view === 'ouvrir-session' && (
                <p className="fdm-header-sub">Nouvelle session de formation</p>
              )}
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {/* ── Body ── */}
        <div className="modal-body fdm-body">

          {successMsg && (
            <div className="fdm-success-banner">{successMsg}</div>
          )}

          {/* ══ PROSPECTS VIEW ══ */}
          {view === 'prospects' && (
            <>
              <div className="fdm-stats-bar">
                <div className="fdm-stat-pill fdm-stat-hot">
                  🔥 <strong>{formation.hot_prospects_count}</strong> prospects chauds
                </div>
                <div className="fdm-stat-pill fdm-stat-total">
                  👥 <strong>{formation.total_prospects_count}</strong> total
                </div>
                <div className="fdm-stat-pill fdm-stat-score">
                  📊 Score moyen <strong>{formation.average_score}%</strong>
                </div>
                <div
                  className="fdm-stat-pill fdm-stat-priority"
                  style={{
                    backgroundColor: (formation.priorite?.color || '#3b82f6') + '20',
                    color: formation.priorite?.color || '#3b82f6',
                    border: `1px solid ${formation.priorite?.color || '#3b82f6'}40`,
                  }}
                >
                  ⚡ Priorité <strong>{formation.priorite_label || formation.priorite?.label}</strong>
                </div>
              </div>

              <div className="fdm-recommendation-card">
                <div className="fdm-rec-icon"></div>
                <div className="fdm-rec-content">
                  <span className="fdm-rec-label">Recommandation</span>
                  <p className="fdm-rec-text">{formation.recommandation_ia}</p>
                </div>
              </div>

              <div className="modal-hot-title">
                🔥 TOP prospects chauds ({formation.hot_prospects_count})
              </div>

              <div className="prospects-table-wrapper">
                <table className="prospects-table">
                  <thead>
                    <tr>
                      <th>Prospect</th>
                      <th>Téléphone</th>
                      <th>Email</th>
                      <th>Dernière activité</th>
                      <th>Score IA</th>
                      <th>Niveau</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prospects.map((prospect) => (
                      <tr key={prospect.id}>
                        <td>{prospect.prenom} {prospect.nom}</td>
                        <td>{prospect.telephone || '—'}</td>
                        <td>{prospect.email || '—'}</td>
                        <td>{new Date(prospect.derniere_activite).toLocaleDateString('fr-FR')}</td>
                        <td>
                          <span className="fdm-score-badge">{prospect.score_ia}%</span>
                        </td>
                        <td>
                          <span className={`level-badge level-${prospect.niveau.toLowerCase()}`}>
                            {prospect.niveau}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* ══ OUVRIR SESSION VIEW ══ */}
          {view === 'ouvrir-session' && (
            <OuvrirSessionForm
              formation={formation}
              formateurs={formateurs}
              onSuccess={handleSessionCreated}
              onCancel={() => setView('prospects')}
            />
          )}
        </div>

        {/* ── Footer ── */}
        {view === 'prospects' && (
          <div className="modal-footer fdm-footer">
            <button className="modal-button btn btn-cancel" onClick={onClose}>Fermer</button>
            {isSuperAdmin && (
              <button
                className="btn fdm-open-session-btn"
                onClick={() => setView('ouvrir-session')}
              >
                <i className="fa-solid fa-calendar-plus"></i>
                Ouvrir une session
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FormationDetailModal;